import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Alert,
  DialogContentText,
  Chip,
  Card,
  CardContent,
  Typography,
  useTheme,
  alpha,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
} from "@mui/material";
import {
  PersonAdd as UserPlusIcon,
  Delete as DeleteIcon,
  Visibility as EyeIcon,
  Group as UsersIcon,
} from "@mui/icons-material";
import { useFormik } from "formik";
import * as yup from "yup";
import axios from "axios";
import { User, EmployeeRequest, Degree, VisaStatus } from "../../types/auth";

const validationSchema = yup.object({
  email: yup
    .string()
    .email("Invalid email format")
    .required("Email is required"),
  firstName: yup.string().required("First name is required"),
  lastName: yup.string().required("Last name is required"),
  password: yup
    .string()
    .min(8, "Password must be at least 8 characters")
    .required("Password is required"),
  degree: yup.string().required("Degree is required"),
  employeeRole: yup.string().required("Employee role is required"),
  visaStatus: yup.string().required("Visa status is required"),
});

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8080/api";

const EmployeesTab: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string>("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const theme = useTheme();

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/employer/employees`);
      setUsers(response.data);
    } catch (err: any) {
      setError(err.message || "Failed to fetch users");
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const formik = useFormik({
    initialValues: {
      email: "",
      firstName: "",
      lastName: "",
      password: "",
      degree: "",
      employeeRole: "",
      visaStatus: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        const employeeRequest: EmployeeRequest = {
          email: values.email,
          password: values.password,
          firstName: values.firstName,
          lastName: values.lastName,
          degree: values.degree as Degree,
          employeeRole: values.employeeRole,
          visaStatus: values.visaStatus as VisaStatus,
        };
        await axios.post(`${API_URL}/employer/employees`, employeeRequest);
        setOpen(false);
        resetForm();
        fetchUsers();
      } catch (err: any) {
        setError(err.message || "Failed to create employee");
      }
    },
  });

  const handleDeleteClick = (user: User) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;

    try {
      await axios.delete(`${API_URL}/employer/employees/${userToDelete.id}`);
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      fetchUsers();
    } catch (err: any) {
      setError(err.message || "Failed to delete employee");
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  const handleViewDetails = (user: User) => {
    setSelectedUser(user);
    setViewDialogOpen(true);
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case "active":
        return "success";
      case "inactive":
        return "error";
      case "pending":
        return "warning";
      default:
        return "default";
    }
  };

  return (
    <Box>
      {/* Header Stats */}
      <Box sx={{ mb: 3 }}>
        <Card sx={{ background: alpha(theme.palette.primary.main, 0.1) }}>
          <CardContent>
            <Box display="flex" alignItems="center">
              <UsersIcon color="primary" sx={{ mr: 2, fontSize: 40 }} />
              <Box>
                <Typography variant="h4" color="primary" gutterBottom>
                  {users.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Employees
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Add Employee Button */}
      <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h6" sx={{ display: "flex", alignItems: "center" }}>
          <UsersIcon color="primary" sx={{ mr: 1 }} />
          Employee Directory
        </Typography>
        <Button
          variant="contained"
          startIcon={<UserPlusIcon />}
          onClick={() => setOpen(true)}
        >
          Add New Employee
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Enhanced Employee Table */}
      <TableContainer
        component={Card}
        sx={{
          borderRadius: 2,
          boxShadow: theme.shadows[4],
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.1) }}>
              <TableCell><strong>Employee</strong></TableCell>
              <TableCell><strong>Email</strong></TableCell>
              <TableCell><strong>Role</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow
                key={user.id}
                hover
                sx={{ "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.05) } }}
              >
                <TableCell>
                  <Box>
                    <Typography variant="body1" fontWeight={500}>
                      {user.firstName} {user.lastName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ID: {user.id}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  <Chip
                    label={user.role}
                    variant="outlined"
                    size="small"
                    color="primary"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={user.status || "Active"}
                    color={getStatusColor(user.status) as any}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Box display="flex" gap={1}>
                    <IconButton
                      color="primary"
                      onClick={() => handleViewDetails(user)}
                      size="small"
                    >
                      <EyeIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => handleDeleteClick(user)}
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add Employee Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Employee</DialogTitle>
        <form onSubmit={formik.handleSubmit}>
          <DialogContent>
            <TextField
              fullWidth
              margin="normal"
              id="email"
              name="email"
              label="Email"
              value={formik.values.email}
              onChange={formik.handleChange}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
            />
            <TextField
              fullWidth
              margin="normal"
              id="firstName"
              name="firstName"
              label="First Name"
              value={formik.values.firstName}
              onChange={formik.handleChange}
              error={
                formik.touched.firstName && Boolean(formik.errors.firstName)
              }
              helperText={formik.touched.firstName && formik.errors.firstName}
            />
            <TextField
              fullWidth
              margin="normal"
              id="lastName"
              name="lastName"
              label="Last Name"
              value={formik.values.lastName}
              onChange={formik.handleChange}
              error={
                formik.touched.lastName && Boolean(formik.errors.lastName)
              }
              helperText={formik.touched.lastName && formik.errors.lastName}
            />
            <TextField
              fullWidth
              margin="normal"
              id="password"
              name="password"
              label="Password"
              type="password"
              value={formik.values.password}
              onChange={formik.handleChange}
              error={
                formik.touched.password && Boolean(formik.errors.password)
              }
              helperText={formik.touched.password && formik.errors.password}
            />
            <FormControl
              fullWidth
              margin="normal"
              error={formik.touched.degree && Boolean(formik.errors.degree)}
            >
              <InputLabel id="degree-label">Degree</InputLabel>
              <Select
                labelId="degree-label"
                id="degree"
                name="degree"
                value={formik.values.degree}
                onChange={formik.handleChange}
                label="Degree"
              >
                <MenuItem value="COMPUTER_SCIENCE">Computer Science</MenuItem>
                <MenuItem value="DATA_SCIENCE">Data Science</MenuItem>
                <MenuItem value="DATA_ANALYTICS">Data Analytics</MenuItem>
              </Select>
              {formik.touched.degree && formik.errors.degree && (
                <FormHelperText>{formik.errors.degree}</FormHelperText>
              )}
            </FormControl>
            <TextField
              fullWidth
              margin="normal"
              id="employeeRole"
              name="employeeRole"
              label="Employee Role"
              value={formik.values.employeeRole}
              onChange={formik.handleChange}
              error={
                formik.touched.employeeRole && Boolean(formik.errors.employeeRole)
              }
              helperText={formik.touched.employeeRole && formik.errors.employeeRole}
            />
            <FormControl
              fullWidth
              margin="normal"
              error={formik.touched.visaStatus && Boolean(formik.errors.visaStatus)}
            >
              <InputLabel id="visa-status-label">Visa Status</InputLabel>
              <Select
                labelId="visa-status-label"
                id="visaStatus"
                name="visaStatus"
                value={formik.values.visaStatus}
                onChange={formik.handleChange}
                label="Visa Status"
              >
                <MenuItem value="US_CITIZEN">US Citizen</MenuItem>
                <MenuItem value="GREEN_CARD">Green Card</MenuItem>
                <MenuItem value="H1B">H1B</MenuItem>
                <MenuItem value="L1">L1</MenuItem>
                <MenuItem value="OPT">OPT</MenuItem>
                <MenuItem value="CPT">CPT</MenuItem>
                <MenuItem value="F1">F1</MenuItem>
                <MenuItem value="J1">J1</MenuItem>
                <MenuItem value="OTHER">Other</MenuItem>
              </Select>
              {formik.touched.visaStatus && formik.errors.visaStatus && (
                <FormHelperText>{formik.errors.visaStatus}</FormHelperText>
              )}
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" variant="contained">
              Add Employee
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* View Employee Details Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Employee Details</DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="h6" gutterBottom>
                {selectedUser.firstName} {selectedUser.lastName}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Email:</strong> {selectedUser.email}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Role:</strong> {selectedUser.role}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Status:</strong> {selectedUser.status || "Active"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Employee ID:</strong> {selectedUser.id}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete {userToDelete?.firstName}{" "}
            {userToDelete?.lastName}? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmployeesTab;